import {
  createContext,
  Dispatch,
  FC,
  SetStateAction,
  useContext,
  useEffect,
  useState,
} from "react";
import { useParams } from "react-router-dom";
import { toast } from "react-toastify";
import {
  deleteObjetivos,
  getObjetivosByOrgId,
  putObjetivoById,
} from "../../../api/broker/objetivo";
import {
  EditObjetivoFields,
  iDataObjetivo,
} from "../../../interfaces/SuperAdmin/Pages/iDataObjetivos";
import { iDataTipo } from "../../../interfaces/SuperAdmin/Pages/iDataTipos";
import { getTiposByOrgId } from "../../../api/broker/tipos";

interface ObjetivosContextProps {
  objetivos: iDataObjetivo[];
  tipos: iDataTipo[];

  toEditObjetivo: iDataObjetivo | null;
  selectedObjetivos: string[];
  setSelectedObjetivos: Dispatch<SetStateAction<string[]>>;
  searchText: string;
  setSearchText: Dispatch<SetStateAction<string>>;

  isEditModalOpen: boolean;
  isDeleteModalOpen: boolean;

  isLoading: boolean;
  isLoadingTipos: boolean;
  isEditing: boolean;
  isDeleting: boolean;

  handleEditObjetivo: (objetivo: iDataObjetivo) => void;
  toggleEditModal: (isOpen: boolean) => void;
  toggleDeleteModal: (isOpen: boolean) => void;

  objetivoEdit: (
    data: EditObjetivoFields,
    id_objetivo: string
  ) => Promise<void>;
  objetivoDelete: (body: { ids: string[] }) => Promise<void>;
}

const ObjetivosContext = createContext<ObjetivosContextProps>(
  {} as ObjetivosContextProps
);

interface Props {
  children: React.ReactNode;
}

const ObjetivosProvider: FC<Props> = ({ children }) => {
  const { idOrganization } = useParams();

  const [objetivos, setObjetivos] = useState<iDataObjetivo[]>([]);
  const [tipos, setTipos] = useState<iDataTipo[]>([]);

  const [toEditObjetivo, setToEditObjetivo] = useState<iDataObjetivo | null>(
    null
  );
  const [selectedObjetivos, setSelectedObjetivos] = useState<string[]>([]);
  const [searchText, setSearchText] = useState("");

  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);

  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingTipos, setIsLoadingTipos] = useState(true);
  const [isEditing, setIsEditing] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  // Función para extraer objetivos
  const fetchObjetivos = async (id: string) => {
    setIsLoading(true);
    try {
      const objetivosRes = await getObjetivosByOrgId(id);
      console.log(objetivosRes);

      if (objetivosRes.data.statusCode === 200) {
        setObjetivos(objetivosRes.data.body);
      } else {
        toast.error("Error al obtener los objetivos de la organización");
      }
    } catch (error) {
      console.log(error);
      toast.error("Error al obtener los objetivos de la organización");
    } finally {
      setIsLoading(false);
    }
  };

  // Función para traer los tipos
  const fetchTipos = async () => {
    if (idOrganization) {
      setIsLoadingTipos(true);
      try {
        const res = await getTiposByOrgId(idOrganization);

        if (res.data.statusCode === 200) {
          setTipos(res.data.body);
        } else {
          toast.error("Error al obtener los tipos de la organización");
        }
      } catch (error) {
        console.log(error);
        toast.error("Error al obtener los tipos de la organización");
      } finally {
        setIsLoadingTipos(false);
      }
    }
  };

  // Edicion de objetivo
  const objetivoEdit = async (
    data: EditObjetivoFields,
    id_objetivo: string
  ) => {
    setIsEditing(true);
    try {
      const res = await putObjetivoById(data, id_objetivo);
      console.log(res);
      if (res.data.statusCode === 200) {
        setObjetivos((prev) =>
          prev.map((objetivo) => {
            if (objetivo.id_objetivo === id_objetivo) {
              return res.data.body;
            }
            return objetivo;
          })
        );
        toggleEditModal(false);
        toast.success("Objetivo editado exitosamente");
      } else {
        toast.error("Error al editar objetivo");
      }
    } catch (error) {
      console.log(error);
      toast.error("Error al editar objetivo");
    } finally {
      setIsEditing(false);
    }
  };

  // Eliminar objetivos
  const objetivoDelete = async (body: { ids: string[] }) => {
    setIsDeleting(true);
    try {
      const res = await deleteObjetivos(body);
      console.log(res);
      if (res.data.statusCode === 200) {
        setSelectedObjetivos([]);
        setObjetivos((prev) =>
          prev.filter((item) => !body.ids.includes(item.id_objetivo))
        );
        toggleDeleteModal(false);
        toast.success("Objetivos eliminados exitosamente");
      } else {
        toast.error("Error al eliminar los objetivos de la organización");
      }
    } catch (error) {
      console.log(error);
      toast.error("Error al eliminar los objetivos de la organización");
    } finally {
      setIsDeleting(false);
    }
  };

  // Abrir menu de editar objetivo
  const toggleEditModal = (isOpen: boolean) => {
    if (isOpen && !tipos.length) {
      fetchTipos();
    }
    if (!isOpen) {
      setToEditObjetivo(null);
    }
    setIsEditModalOpen(isOpen);
  };

  // Abrir menu de eliminar objetivos
  const toggleDeleteModal = (isOpen: boolean) => {
    setIsDeleteModalOpen(isOpen);
  };

  // Seleccionar objetivo a editar
  const handleEditObjetivo = (objetivo: iDataObjetivo) => {
    setToEditObjetivo(objetivo);
    toggleEditModal(true);
  };

  useEffect(() => {
    if (idOrganization) {
      fetchObjetivos(idOrganization);
    }
  }, [idOrganization]);

  return (
    <ObjetivosContext.Provider
      value={{
        objetivos,
        tipos,
        setSelectedObjetivos,
        toEditObjetivo,
        selectedObjetivos,
        searchText,
        setSearchText,
        isEditModalOpen,
        isDeleteModalOpen,
        isLoading,
        isLoadingTipos,
        isEditing,
        isDeleting,
        handleEditObjetivo,
        toggleEditModal,
        toggleDeleteModal,
        objetivoEdit,
        objetivoDelete,
      }}
    >
      {children}
    </ObjetivosContext.Provider>
  );
};

const useObjetivos = () => useContext(ObjetivosContext);

export { ObjetivosProvider, useObjetivos };
